import { CommonModule } from '@angular/common';
import { Component, ViewChild, signal, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Nationality } from '../../../models/nationality';
import { BulkMergeRequest } from '../../../models/bulkMergeRequest';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { LookupTableComponent } from '../../shared/lookup-table/lookup-table.component';
import { LutService } from '../../../services/lut.service';
import { LookupDataService } from '../../../services/lookupDataService.service';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { HotToastService } from '@ngxpert/hot-toast';
import { Helpers } from '../../../utilities/helpers';

@Component({
  selector: 'app-nationality-list',
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    LookupTableComponent,
    NgxSpinnerModule
  ],
  templateUrl: './nationality-list.component.html',
  styleUrl: './nationality-list.component.scss'
})
export class NationalityListComponent implements OnInit {
  @ViewChild(LookupTableComponent) lookupTable!: LookupTableComponent;

  nationalities = signal<Nationality[]>([]);
  matchedItems: Nationality[] | undefined = undefined;
  rightItem: Nationality | undefined = undefined;

  constructor(
    private confirmationDialogService: ConfirmationDialogService,
    private lutService: LutService,
    private lookupDataService: LookupDataService,
    private spinnerService: NgxSpinnerService,
    private toast: HotToastService
  ) {}

  ngOnInit(): void {
    this.loadNationalities();
  }

  loadNationalities(): void {
    this.spinnerService.show();
    this.lutService.getNationalities().subscribe({
      next: (data) => {
        // Sort alphabetically by name before setting
        const sortedData = this.sortNationalities(data);
        this.nationalities.set(sortedData);
        this.spinnerService.hide();
      },
      error: (error) => {
        console.error('Error loading nationalities:', error);
        this.spinnerService.hide();
        const errorMessage = Helpers.parseApiError(error);
        this.toast.error(`<strong>Failed to load Nationalities:</strong></br></br> ${errorMessage}`);
      }
    });
  }

  private sortNationalities(nationalities: Nationality[]): Nationality[] {
    return [...nationalities].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    );
  }

  onCreateNationality(): void {
    this.lookupTable.startAdd();
  }

  onItemSelected(nationality: Nationality): void {
  }

  onEditNationality(nationality: Nationality): void {
  }

  onDeleteNationality(nationality: Nationality): void {
    this.confirmationDialogService.openDeleteConfirmationDialog(nationality.name)
      .subscribe(result => {
        if (result) {
          // Store original list for rollback in case of error
          const originalNationalities = this.nationalities();

          // 1. Optimistically remove from local state immediately
          this.nationalities.update(nationalities => nationalities.filter(n => n.id !== nationality.id));

          // 2. Call backend API
          this.spinnerService.show();
          this.lutService.deleteNationality(nationality.id).subscribe({
            next: () => {
              this.spinnerService.hide();
              this.toast.success('Nationality deleted successfully');
              // 3. Update cache so other components get updated data
              this.lookupDataService.updateTableData('nationalities', this.nationalities());
            },
            error: (error) => {
              console.error('Error deleting nationality:', error);
              this.spinnerService.hide();
              // 4. On error: Restore original list
              this.nationalities.set(originalNationalities);
              const errorMessage = Helpers.parseApiError(error);
              this.toast.error(`<strong>Failed to delete Nationality:</strong></br></br> ${errorMessage}`);
            }
          });
        }
      });
  }

  onItemSave(event: {item: Nationality, newName: string} | {item: Nationality, newValue: string, columnKey: string}): void {
    // Store original item for rollback in case of error
    const originalItem = { ...event.item };

    // Prepare updated item
    let updatedItem: Nationality;
    if ('newName' in event) {
      // Legacy format - simple name update
      updatedItem = { ...event.item, name: event.newName };
    } else {
      // New format - update specific column
      updatedItem = { ...event.item, [event.columnKey]: event.newValue };
    }

    // 1. Optimistically update local state immediately and re-sort
    this.nationalities.update(nationalities => {
      const updated = nationalities.map(n => n.id === event.item.id ? updatedItem : n);
      return this.sortNationalities(updated);
    });

    // 2. Call backend API
    this.spinnerService.show();
    this.lutService.updateNationality(event.item.id, updatedItem).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Nationality updated successfully');
        // 3. On success: Update with server response and re-sort to maintain alphabetical order
        this.nationalities.update(nationalities => {
          const updated = nationalities.map(n => n.id === event.item.id ? response : n);
          return this.sortNationalities(updated);
        });
        // 4. Update cache so other components get updated data
        this.lookupDataService.updateTableData('nationalities', this.nationalities());
      },
      error: (error) => {
        console.error('Error updating nationality:', error);
        this.spinnerService.hide();
        // 4. On error: Rollback to original state
        this.nationalities.update(nationalities =>
          nationalities.map(n => n.id === event.item.id ? originalItem : n)
        );
        const errorMessage = Helpers.parseApiError(error);
        this.toast.error(`<strong>Failed to update Nationality:</strong></br></br> ${errorMessage}`);
      }
    });
  }

  onReadyToMatch(event: {matchedItems?: Nationality[], rightItem?: Nationality}): void {
    this.matchedItems = event.matchedItems;
    this.rightItem = event.rightItem;
  }

  onNationalityCorrected(): void {
    if (!this.rightItem || !this.matchedItems || this.matchedItems.length === 0) {
      console.error('Missing required data for merge operation');
      this.toast.error('Please select items to merge');
      return;
    }

    const bulkMergeRequest: BulkMergeRequest = {
      leftIds: this.matchedItems.map(item => item.id),
      rightId: this.rightItem.id
    };

    const itemsToMerge = this.matchedItems.map(item => item.name).join(', ');
    this.confirmationDialogService.openConfirmationDialog(
      'Confirm Merge',
      `Are you sure you want to merge "${itemsToMerge}" into "${this.rightItem.name}"? This action cannot be undone.`,
      'Merge',
      'Cancel',
      'merge_type',
      'clear'
    ).subscribe(result => {
      if (result && this.rightItem) {
        const originalNationalities = this.nationalities();

        // Optimistically remove matched items from UI
        this.nationalities.update(nationalities =>
          nationalities.filter(n => !bulkMergeRequest.leftIds.includes(n.id))
        );

        this.rightItem = undefined;
        this.matchedItems = undefined;

        this.spinnerService.show();
        this.lutService.mergeNationalitiesBulk(bulkMergeRequest).subscribe({
          next: () => {
            this.spinnerService.hide();
            this.toast.success('Nationalities merged successfully');
            this.lookupTable.clearMatchingState();
            this.loadNationalities();
            this.lookupDataService.updateTableData('nationalities', this.nationalities());
          },
          error: (error) => {
            console.error('Error merging nationalities:', error);
            this.spinnerService.hide();
            this.nationalities.set(originalNationalities);
            this.rightItem = originalNationalities.find(n => n.id === bulkMergeRequest.rightId);
            this.matchedItems = originalNationalities.filter(n => bulkMergeRequest.leftIds.includes(n.id));
            this.toast.error(`<strong>Failed to merge Nationalities:</strong></br></br> ${Helpers.parseApiError(error)}`);
          }
        });
      }
    });
  }

  onItemAdd(event: {[key: string]: any}): void {
    // Generate temporary ID for optimistic update
    const tempId = crypto.randomUUID();
    const newNationality: Nationality = { id: tempId, name: event['name'], systemReserved: false };

    // 1. Optimistically add to local state immediately and sort
    this.nationalities.update(nationalities => this.sortNationalities([...nationalities, newNationality]));

    // 2. Call backend API
    this.spinnerService.show();
    this.lutService.createNationality({ name: event['name'], systemReserved: false }).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Nationality created successfully');
        // 3. On success: Replace temp item with server response and re-sort to maintain alphabetical order
        this.nationalities.update(nationalities => {
          const updated = nationalities.map(n => n.id === tempId ? response : n);
          return this.sortNationalities(updated);
        });
        // 4. Update cache so other components get updated data
        this.lookupDataService.updateTableData('nationalities', this.nationalities());
      },
      error: (error) => {
        console.error('Error creating nationality:', error);
        this.spinnerService.hide();
        // 4. On error: Remove the temp item
        this.nationalities.update(nationalities =>
          nationalities.filter(n => n.id !== tempId)
        );
        const errorMessage = Helpers.parseApiError(error);
        this.toast.error(`<strong>Failed to create Nationality:</strong></br></br> ${errorMessage}`);
      }
    });
  }
}
